import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Icon,
  Segment
} from 'semantic-ui-react'
import { isNil } from 'ramda'
// Local
import { formatDob } from '../common/formatUtils'
import {
  LIST_PAGE_SIZE,
  LIST_MAX_PAGES,
  FORMAT,
  PATIENT,
  shouldRenderReskin2025
} from '../../constant'
import {
  loadPatients,
  LoadPatientsProps
} from '../../action/patient'
import {
  loadEncounters,
  updateList
} from '../../action/encounter'
import SubHeader from '../common/SubHeader'
import Table from '../common/Table'
import Filter from '../common/filter'
import { patientMrn } from '../common/patientUtils'
import {
  allowPatientTabSchedulingCdi,
  patientOffshoreFilter,
  patientRegionFilter
} from '../common/currentUserUtils'
import {
  cursorClass,
  defaultSort,
  filterMetaData,
  filterMetaDataWithReview,
  INITIAL_PAGES_DISPLAYED
} from './utils'
import PatientScheduleModal from './patientScheduleModal'
import { semiColonsToLineBreaks } from '../v2/utils/utils'
import {
  isCdiWorkerNow,
  isCdiQaManagerNow
} from '../common/roleUtils'
import {
  PatientListFilter,
  User
} from '../authenticated/types'
// Local
import './index.css'
import { Patient } from './patient'
import If from '../common/if'
import { generateQueryFilters } from '../common/generateQueryFilters'
import FilterIcon from '../icons/Filter'
import ReveleerControlButton from '../common/ReveleerControlButton'
import { RootState } from '../../store'

// export default connect(
//   state => ({
//     user: state.user,
//     list: state.patient.list,
//     encounterList: state.encounter.list
//   }), { updateUserPatientListFilters }
// )(PatientList)

const mapStateToProps = (state: RootState) => ({
  user: state.user,
  list: state.patient.list as Patient[],
  encounterList: state.encounter.list
})

type ReduxStateProps = ReturnType<typeof mapStateToProps>

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeTable = Table as unknown as ComponentType<any>

const getEmptyStringFromNull = (value: string | null) => isNil(value)
  ? ''
  : value

/** Map patient data into the proper display format. */
const generateTableData = (patients: Patient[]) =>
  patients.map(patient => {
    const mrn = patientMrn(patient) || ''
    const result = {
      id: patient.id,
      name: `${patient.firstName} ${patient.lastName}`,
      birthDate: isNil(patient.birthDate)
        ? ''
        : formatDob(patient.birthDate, FORMAT.DATE),
      mrn: semiColonsToLineBreaks(mrn),
      email: getEmptyStringFromNull(patient.personalEmail),
      phone: getEmptyStringFromNull(patient.homePhone),
      insurance: getEmptyStringFromNull(patient.insurance),
      prevEncounter: isNil(patient.prevEncounter) ? null : patient.prevEncounter,
      proposedEncounter: isNil(patient.proposedEncounter) ? null : patient.proposedEncounter,
      nextEncounter: isNil(patient.nextEncounter) ? null : patient.nextEncounter,
      meta: {
        className: cursorClass
      }
    }
    return result
  })

const shouldShowScheduleButton = (user: User): boolean => (
  allowPatientTabSchedulingCdi(user) && (isCdiWorkerNow(user.type) || isCdiQaManagerNow(user.type))
)

function computerColumns(user: User) {
  const baseColumns = [
    { name: 'Name', value: 'name' },
    { name: 'DOB', value: 'birthDate' },
    { name: 'MRN(s)', value: 'mrn' },
    { name: 'Email', value: 'email' },
    { name: 'Phone', value: 'phone' }
  ]

  if (shouldShowScheduleButton(user)) {
    baseColumns.push({ name: 'Upcoming Appointment', value: 'nextEncounter' })
    baseColumns.push({ name: 'Schedule New Appointment', value: 'newAppointment' })
    return baseColumns
  }
  baseColumns.push({ name: 'Appointment', value: 'nextEncounter' })
  return baseColumns
}

/**
 * We need to determine if the filter section should be expanded or collapsed.
 * We want to selectively remove the defaultPatientOffshoreFilter since that is a
 * invisible default. Thus it should not affect the expanded or collapsed state.
 * @param {*} o
 * @returns edited filter object
 */
const filterOutDefault = (o: PatientListFilter) => {
  const safeCopy = { ...o }
  delete safeCopy.defaultPatientOffshoreFilter
  return safeCopy
}

const hasPatientFilters = (filters: PatientListFilter) => {
  if (filters === undefined) {
    return false
  }
  return Object
    .values(filterOutDefault(filters))
    .some(x => x !== null && x !== '')
}

type Props = ReduxStateProps & {
  displayModal: boolean
  updateUserPatientListFilters: any
  historInReduxy: any
}


/** Display a list of patients */
function PatientList(
  props: Props
) {
  const {
    displayModal: shouldDisplayModal,
    list,
    user,
    encounterList,
    updateUserPatientListFilters,
    historInReduxy
  } = props
  const hasPatientListFilters = hasPatientFilters(user.patientListFilters)
  // Whether to display the advanced search filters or not
  const [
    shouldDisplayFilterState,
    setShouldshouldDisplayFilterState
  ] = useState(hasPatientListFilters)
  // Values specified in the advanced search filters
  const [
    filtersState,
    setFiltersState
  ] = useState<PatientListFilter>(user.patientListFilters)
  // How many pages or results are currently displayed
  const [
    pagesDisplayedState,
    setPagesDisplayedState
  ] = useState<number>(
    INITIAL_PAGES_DISPLAYED
  )
  // Determine whether to display a specific patients encounters in a modal or not
  const [ , setShouldDisplayModalState ] =
    useState(shouldDisplayModal || false)
  // Patients encounters to display in a modal.
  const [ , setPatientSelectedState ] = useState<string | undefined>(undefined)
  // Controls whether we query for onshore/offshore patients
  const patientOffshoreFilterValue = patientOffshoreFilter(user)
  // Region limitations set by the admin
  const patientRegionFilterValue = patientRegionFilter(user)

  /** add the offshore filter if it is defined */
  const addOffshoreFilterAsQuery = (o: PatientListFilter) => {
    // patientOffshoreFilterValue is an array
    if (patientOffshoreFilterValue) {
      const original = o.query
      // eslint-disable-next-line no-param-reassign
      o.query = {
        ...original,
        onOffShore: patientOffshoreFilterValue
      }
    }
    return o
  }

  /** add the offshore filter if it is defined */
  const addOffshoreFilter = (o: PatientListFilter) => {
    const safe = { ...o }
    // patientOffshoreFilterValue is an array
    if (patientOffshoreFilterValue) {
      safe.onOffShore = patientOffshoreFilterValue
    }
    return safe
  }

  /** add the default region filter if necessary */
  const addRegionFilterAsQuery = (
    o: PatientListFilter
  ): PatientListFilter => {
    if (
      Object.prototype.hasOwnProperty.call(o.query, 'patientRegion') ||
      patientRegionFilterValue === null
    ) {
      // if they explicitly chose something, go with that region
      return o
    }
    // patientRegionFilterValue is an array
    return {
      ...o,
      query: {
        ...o.query,
        patientRegion: patientRegionFilterValue
      }
    }
  }

  /** add the region filter if it is defined */
  const addRegionFilter = (o: PatientListFilter) => {
    if (
      Object.prototype.hasOwnProperty.call(o, 'patientRegion') ||
      patientRegionFilterValue === null
    ) {
      // if they explicitly chose a region, we will go with that choice
      return o
    }
    // patientRegionFilterValue is an array
    return {
      ...o,
      patientRegion: patientRegionFilterValue
    }
  }

  const addPatientStatusFilter = (
    o: PatientListFilter
  ): PatientListFilter => ({
    ...o,
    patientStatus: PATIENT.STATUS.ACTIVE
  })

  /** Load the default search parameters for patient data. */
  useEffect(() => {
    const loadArgs = {
      query: addPatientStatusFilter(
        generateQueryFilters(
          filterMetaData,
          filterOutDefault(user.patientListFilters)
        )
      ),
      sort: defaultSort,
      userRole: user.type
    }
    loadPatients(
      addRegionFilterAsQuery(
        addOffshoreFilterAsQuery(loadArgs)
      ) as LoadPatientsProps
    )
  }, [])

  /** Toggle whether to display the advanced search or not */
  const toggleFilter = () => {
    setShouldshouldDisplayFilterState(!shouldDisplayFilterState)
  }

  const appendFilterMetaData = (currentUser: User) => {
    if (allowPatientTabSchedulingCdi(currentUser)) {
      return filterMetaDataWithReview
    }
    return filterMetaData
  }

  /** Track any modifications to filter search values */
  const onChangeFilter = (
    name: string,
    value: string
  ) => {
    setFiltersState(
      Object.assign(
        {},
        filtersState,
        {
          [name]: value
        }
      )
    )
  }

  /** Load additional patients based on existing search criteria. */
  function onMore() {
    const q = generateQueryFilters(filterMetaData,
      addRegionFilter(addOffshoreFilter(addPatientStatusFilter(filtersState))))
    const loadArgs = {
      query: q,
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * pagesDisplayedState + 1,
      sort: defaultSort,
      append: true,
      userRole: user.type
    }
    loadPatients(loadArgs)
    setPagesDisplayedState(pagesDisplayedState + 1)
  }

  /** Initial search for patients for a given set of filters. */
  function searchWithFilter(
    values: PatientListFilter
  ) {
    const q = addRegionFilter(addOffshoreFilter(addPatientStatusFilter(values)))
    const loadArgs = {
      query: q,
      sort: defaultSort,
      userRole: user.type
    }
    setPagesDisplayedState(INITIAL_PAGES_DISPLAYED)
    loadPatients(loadArgs)
    setShouldDisplayModalState(true)
  }

  /**
    Select a patient.  When selected,  display set of encounters for that patient.
    NOTE we only display the first 200 encounters.  Can be improved.
  */
  const selectRecord = (id: string) => {
    setShouldDisplayModalState(true)
    setPatientSelectedState(id)
    updateList([], false, undefined)
    loadEncounters({
      // SHAWN userRole does not exist!!!!!
      // userRole: user.userRole,
      userRole: user.type,
      query: { patientId: id },
      limit: LIST_PAGE_SIZE * LIST_MAX_PAGES,
      offset: 0,
      sort: 'start asc',
      append: undefined
    })
  }

  /** Close patient modal. */
  function closeModal() {
    setShouldDisplayModalState(false)
    setPatientSelectedState(undefined)
  }

  // const { pagesDisplayed, filters, displayModal } = state
  const tableData = generateTableData(list)
  const filterButton = shouldRenderReskin2025
    ? (
      <ReveleerControlButton
        icon={ FilterIcon }
        id="worklist-filter"
        isDisabled={ false }
        onClick={ toggleFilter }
      />
    )
    : (
      <Icon
        name="filter"
        className="pointer"
        size="large"
        onClick={ toggleFilter }
      />
    )
  let shouldDisplayMore
  if (tableData.length > LIST_PAGE_SIZE * pagesDisplayedState) {
    shouldDisplayMore = pagesDisplayedState < LIST_MAX_PAGES
    tableData.pop()
  }
  const filterMetaDataList = appendFilterMetaData(user)

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left="Patients"
          right={ filterButton }
        />
        <If isTrue={ shouldDisplayFilterState }>
          <Filter
            initialValues={ filtersState }
            setInitialValuesState={ setFiltersState }
            updateUserPatientListFilters={ updateUserPatientListFilters }
            onChange={ onChangeFilter }
            submit={ searchWithFilter }
            filters={ filterMetaDataList }
            onClear={ undefined }
            buttonText=""
          />
        </If>
        <SafeSegment attached className="no-margin">
          <SafeTable
            computerColumns={ computerColumns(user) }
            mobileColumns={ [
              { value: 'name', width: '11' },
              { value: 'birthDate', width: '5' }
            ] }
            data={ tableData }
            onClick={ selectRecord }
            more={ shouldDisplayMore }
            // max={ displayMaxRecords }
            onMore={ onMore }
          />
        </SafeSegment>
        <If isTrue={ shouldDisplayModal }>
          <PatientScheduleModal
            list={ encounterList as any }
            history={ historInReduxy }
            closeModal={ closeModal }
          />
        </If>
      </Container>
    </div>
  )
}

export default connect(mapStateToProps)(PatientList)
